/**
 * Jobs page
 * List and manage all scraping jobs
 */

import { useState } from 'react';
import { Button, Space, Segmented } from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  UnorderedListOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/common/PageHeader';
import { JobsTable, JobCard, JobFilters } from '@/components/jobs';
import { useJobs, usePauseJob, useResumeJob, useRetryJob, useDeleteJob } from '@/hooks/useJobs';
import type { Job, JobStatus } from '@/types/job';

type ViewMode = 'table' | 'cards';

export function JobsPage() {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [filters, setFilters] = useState<{
    search?: string;
    status?: JobStatus[];
    dateRange?: [Date, Date];
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }>({});

  const {
    data: jobsData,
    isLoading,
    refetch,
  } = useJobs({
    status: filters.status,
    search: filters.search || undefined,
  });

  const handleClearFilters = () => {
    setFilters({});
  };

  const pauseJob = usePauseJob();
  const resumeJob = useResumeJob();
  const retryJob = useRetryJob();
  const deleteJob = useDeleteJob();

  // These handlers accept string IDs to match JobsTable interface
  const handlePause = async (id: string) => {
    await pauseJob.mutateAsync(id);
  };

  const handleResume = async (id: string) => {
    await resumeJob.mutateAsync(id);
  };

  const handleRetry = async (id: string) => {
    await retryJob.mutateAsync(id);
  };

  const handleDelete = async (id: string) => {
    await deleteJob.mutateAsync(id);
  };

  const handleViewDetails = (id: string) => {
    navigate(`/jobs/${id}`);
  };

  // Job object handlers for JobCard component
  const handlePauseJob = async (job: Job) => handlePause(job.id);
  const handleResumeJob = async (job: Job) => handleResume(job.id);
  const handleRetryJob = async (job: Job) => handleRetry(job.id);
  const handleDeleteJob = async (job: Job) => handleDelete(job.id);
  const handleViewDetailsJob = (job: Job) => handleViewDetails(job.id);

  const jobs = jobsData?.jobs || [];

  return (
    <div>
      <PageHeader
        title="Jobs"
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => refetch()}
              loading={isLoading}
            >
              Refresh
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/jobs/new')}
            >
              New Job
            </Button>
          </Space>
        }
      />

      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        {/* Toolbar */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 16,
            flexWrap: 'wrap',
          }}
        >
          <JobFilters
            filters={filters}
            onChange={setFilters}
            onClear={handleClearFilters}
            loading={isLoading}
          />

          <Segmented
            value={viewMode}
            onChange={(value) => setViewMode(value as ViewMode)}
            options={[
              { value: 'table', icon: <UnorderedListOutlined /> },
              { value: 'cards', icon: <AppstoreOutlined /> },
            ]}
          />
        </div>

        {/* Content */}
        {viewMode === 'table' ? (
          <JobsTable
            jobs={jobs}
            loading={isLoading}
            onPause={handlePause}
            onResume={handleResume}
            onRetry={handleRetry}
            onDelete={handleDelete}
            onViewDetails={handleViewDetails}
          />
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
              gap: 16,
            }}
          >
            {jobs.map((job) => (
              <JobCard
                key={job.id}
                job={job}
                onPause={handlePause}
                onResume={handleResume}
                onRetry={handleRetry}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </Space>
    </div>
  );
}

export default JobsPage;
